// ─────────────────────────────────────────────────────────────────────────────
// canonicalTopologyAdapter — the ONE mapping from Rexy OS truth to graph records.
//
// WHAT THIS REPLACES. The G2.1/G2.2 live path reconstructed Rexy OS in the
// frontend: it walked `domains` and `agents` arrays, joined them against the
// app's own roster to decide what existed, and hand-assembled memory, workflow
// and service children. That produced the 103-node map that was wrong — it could
// not see the eleven-strong CODE build team, and it rendered DOCTOR as a live
// Personal agent because a validation roster listed the name.
//
// THIS ADAPTER DECIDES NOTHING. It maps. The backend's CanonicalTopologySnapshot
// already answered what exists, who owns it, what state it is in and why; every
// node here is one canonical entity and every edge is one canonical relation.
// There is no roster, no join against lib/agents, no inference of parentage from
// a naming convention, and no list of domains anywhere in this file. If an
// entity is absent from the DTO it is absent from the galaxy — that is the point.
//
// GENERIC OVER KIND. The mapping is table-driven, so a kind the backend adds
// later arrives as a node without an edit here. An UNRECOGNISED kind is rendered
// as 'unknown' and reported, never silently dropped: a node the frontend cannot
// classify is a finding, and dropping it would recreate the exact blindness this
// pass exists to remove.
//
// PRESENTATION MAY BE LOCAL. IDENTITY AND EXISTENCE MAY NOT. Sizing, importance
// and pick radius are presentation and are decided here. Identity, label,
// parentage, ownership and implementation state come from the DTO and are copied
// verbatim.
// ─────────────────────────────────────────────────────────────────────────────

// TYPE-ONLY, DELIBERATELY. Every name below is a type, so `import type` lets the
// node test harness erase the statement outright instead of resolving an
// extensionless specifier it cannot see — while CRA compiles it unchanged. This
// is the same constraint mediaProbe.test.mts documents, solved at the import
// rather than with a per-call-site ts-ignore.
import type {
  RexyGraphEdgeRecord,
  RexyGraphNodeKind,
  RexyGraphNodeRecord,
  RexyGraphRelationKind,
  RexyGraphSnapshot,
} from './graphTypes';

/** One entity exactly as core/os_topology.py emits it. */
export interface CanonicalEntity {
  id: string;
  label: string;
  kind: string;
  implementationState: string;
  evidence?: Array<{ source: string; type: string }>;

  ownerId?: string;
  ownerKind?: string;
  domainId?: string;
  domainKey?: string;
  teamId?: string;

  // Memory / store structural + TEMPORAL facts. Never contents.
  path?: string;
  storeKind?: string;
  bytes?: number | null;
  mtime?: number | null;
  records?: number | null;
  recordState?: string;
  encrypted?: boolean;
  partitionOf?: string;
  isolation?: string;
  vectorShared?: boolean;

  subKind?: string;
  engine?: string;
  provider?: string;
  mechanism?: string;
  surfaceKind?: string;
  backingStoreExists?: boolean | null;
  registryAgents?: number;
  builtAgents?: number;
  memberCount?: number;
}

export interface CanonicalRelation {
  id: string;
  sourceId: string;
  targetId: string;
  kind: string;
}

export interface CanonicalSnapshotDto {
  revision: string;
  generatedAt?: string | null;
  entities: CanonicalEntity[];
  relations: CanonicalRelation[];
  counts: Record<string, number>;
  drift?: Array<{ entity: string; finding: string; classification: string }>;
}

export const OS_ROOT_ID = 'os:root';

export const LIVE_BUILT = 'LIVE_BUILT';
export const DECLARED_NOT_BUILT = 'DECLARED_NOT_BUILT';
export const TEAM_MEMBER_STATE = 'TEAM_MEMBER';
export const ORPHAN_STATE = 'ORPHAN';

/**
 * Kind → presentation. `importance` drives visual scale and LOD participation;
 * `pick` is the CSS-pixel hit diameter, deliberately larger than the drawn point
 * because a 3px star is not a 3px target. `level` is the semantic level at which
 * the kind first appears.
 */
interface KindSpec {
  node: RexyGraphNodeKind;
  importance: number;
  pick: number;
  level: 0 | 1 | 2 | 3 | 4;
}

const KINDS: Record<string, KindSpec> = {
  'os-root':       { node: 'os-root',       importance: 1.00, pick: 32, level: 0 },
  'domain':        { node: 'domain',        importance: 0.90, pick: 30, level: 0 },
  'primary-agent': { node: 'primary-agent', importance: 0.70, pick: 24, level: 0 },
  'team':          { node: 'team',          importance: 0.65, pick: 22, level: 0 },
  'team-member':   { node: 'team-member',   importance: 0.50, pick: 18, level: 1 },
  'domain-memory': { node: 'domain-memory', importance: 0.55, pick: 18, level: 1 },
  'agent-memory':  { node: 'agent-memory',  importance: 0.40, pick: 16, level: 2 },
  'team-memory':   { node: 'team-memory',   importance: 0.40, pick: 16, level: 2 },
  'os-memory':     { node: 'os-memory',     importance: 0.55, pick: 18, level: 1 },
  'database':      { node: 'database',      importance: 0.35, pick: 15, level: 2 },
  'model':         { node: 'model',         importance: 0.35, pick: 15, level: 2 },
  'workflow':      { node: 'workflow',      importance: 0.35, pick: 14, level: 2 },
  'service':       { node: 'service',       importance: 0.45, pick: 14, level: 1 },
  'surface':       { node: 'surface',       importance: 0.30, pick: 14, level: 2 },
  'memory-file':   { node: 'memory-file',   importance: 0.20, pick: 11, level: 3 },
  'memory-record': { node: 'memory-record', importance: 0.15, pick: 10, level: 4 },
};

const RELATIONS: Record<string, RexyGraphRelationKind> = {
  owns: 'owns',
  'member-of': 'contains',
  'contributes-to': 'contributes-to',
  'indexed-by': 'indexed-by',
  uses: 'uses',
  exposes: 'surface-of',
};

export interface AdapterReport {
  /** Kinds the backend emitted that this adapter has no spec for. */
  unmappedKinds: string[];
  /** Relation kinds with no mapping — rendered as 'related'. */
  unmappedRelations: string[];
  /** Entities excluded from the LIVE galaxy, and why. */
  excludedFromLive: Array<{ id: string; state: string }>;
  /** Relations whose endpoints are not both present. */
  danglingRelations: string[];
}

export interface AdapterResult {
  snapshot: RexyGraphSnapshot;
  report: AdapterReport;
  /** Every canonical entity, including ones held out of the live galaxy. */
  allEntities: CanonicalEntity[];
}

/**
 * Which entities belong in the NORMAL live galaxy.
 *
 * DECLARED_NOT_BUILT and ORPHAN are real canonical findings and they are carried
 * through to Dev and the Inspector — but they are not operational topology, and
 * rendering DOCTOR beside SPENCER would tell the owner something false. The
 * filter is on STATE, never on a name: nothing here knows what a doctor is.
 */
export function isLiveTopology(e: CanonicalEntity): boolean {
  return e.implementationState === LIVE_BUILT
    || e.implementationState === TEAM_MEMBER_STATE
    || e.implementationState === 'SERVICE';
}

/**
 * Structural parentage, from the RELATIONS the backend proved.
 *
 * Relations are the relationship truth (brief §8); `ownerId`/`teamId`/`domainId`
 * are supporting metadata that some entity kinds simply do not carry. Domains
 * are exactly that case: a domain has no ownerId at all, and its only statement
 * of parentage is `os:root -owns-> os:domain:X`. Deriving parentId from the
 * metadata alone left all nine domains unparented and therefore unreachable from
 * the root in the tree — caught by the reachability assertion, not by eye.
 */
function parentIndex(relations: CanonicalRelation[]): Map<string, string> {
  const parent = new Map<string, string>();
  for (const r of relations) {
    // `owns` and `member-of` are the two containment relations. `contributes-to`,
    // `indexed-by`, `uses` and `exposes` are associations, not parentage, and
    // treating them as such would give a node two homes.
    if (r.kind === 'owns' || r.kind === 'member-of') {
      if (!parent.has(r.targetId)) parent.set(r.targetId, r.sourceId);
    }
  }
  return parent;
}

function mapNode(e: CanonicalEntity, spec: KindSpec,
                 parents: Map<string, string>): RexyGraphNodeRecord {
  // Relation-proved containment first; the metadata fields are the fallback for
  // anything the relation set does not cover. No convention, no name matching.
  const parentId = parents.get(e.id) || e.ownerId || e.teamId || e.domainId || undefined;

  return {
    id: e.id,
    label: e.label,
    kind: spec.node,
    parentId,
    domainId: e.domainId,
    ownerId: e.ownerId,
    importance: spec.importance,
    existing: true,
    metadata: {
      // Copied verbatim from canonical truth — the Inspector, hover, search and
      // the tree all read THESE, so there is one set of facts on screen.
      canonicalKind: e.kind,
      implementationState: e.implementationState,
      domainKey: e.domainKey,
      teamId: e.teamId,
      ownerKind: e.ownerKind,
      evidence: e.evidence,
      // Memory: structure and TIME, never contents.
      path: e.path,
      storeKind: e.storeKind,
      bytes: e.bytes ?? null,
      mtime: e.mtime ?? null,
      // NORMALISED TO AN EXPLICIT null. The backend omits the key entirely when a
      // count is unknowable (an encrypted silo), because its entity builder drops
      // None. Absent and "unknown" are different statements, and a consumer that
      // sees `undefined` cannot tell "not counted" from "field not applicable" —
      // so UNKNOWN is made explicit here. `recordState` carries the same fact in
      // words and is the field to branch on.
      records: e.records ?? null,
      recordState: e.recordState ?? (e.records == null ? 'UNKNOWN' : 'counted'),
      encrypted: e.encrypted,
      partitionOf: e.partitionOf,
      isolation: e.isolation,
      vectorShared: e.vectorShared,
      // Everything else structural.
      subKind: e.subKind,
      engine: e.engine,
      provider: e.provider,
      mechanism: e.mechanism,
      surfaceKind: e.surfaceKind,
      backingStoreExists: e.backingStoreExists,
      registryAgents: e.registryAgents,
      builtAgents: e.builtAgents,
      memberCount: e.memberCount,
      // Presentation, decided here and labelled as such.
      pickDiameterPx: spec.pick,
      semanticLevel: spec.level,
    },
  };
}

/**
 * Build graph records from the canonical DTO.
 *
 * `includeNonLive` exists for Dev/parity, which legitimately wants to SEE the
 * declared-not-built and orphan entities. The normal live galaxy never sets it.
 */
export function buildCanonicalSnapshot(
  dto: CanonicalSnapshotDto,
  opts: { includeNonLive?: boolean } = {},
): AdapterResult {
  const report: AdapterReport = {
    unmappedKinds: [],
    unmappedRelations: [],
    excludedFromLive: [],
    danglingRelations: [],
  };

  const entities = Array.isArray(dto.entities) ? dto.entities : [];
  const relations = Array.isArray(dto.relations) ? dto.relations : [];
  const parents = parentIndex(relations);
  const nodes: RexyGraphNodeRecord[] = [];
  const present = new Set<string>();

  for (const e of entities) {
    if (!opts.includeNonLive && !isLiveTopology(e)) {
      report.excludedFromLive.push({ id: e.id, state: e.implementationState });
      continue;
    }

    let spec = KINDS[e.kind];
    if (!spec) {
      // Reported, then rendered as 'unknown'. A kind the frontend does not
      // recognise is a gap between the two halves of the system, and silently
      // dropping it is how a graph starts lying by omission.
      report.unmappedKinds.push(e.kind);
      spec = { node: 'unknown', importance: 0.3, pick: 14, level: 2 };
    }

    const node = mapNode(e, spec, parents);
    if (!isLiveTopology(e)) {
      // Visible only in Dev, and marked so nothing can mistake it for live.
      node.metadata = { ...node.metadata, nonLive: true };
      node.orphan = e.implementationState === ORPHAN_STATE;
    }
    nodes.push(node);
    present.add(e.id);
  }

  const edges: RexyGraphEdgeRecord[] = [];
  for (const r of relations) {
    // An edge to an entity that is not in this galaxy is dropped, not pointed at
    // nothing. This is how holding DOCTOR out of live mode also removes the edge
    // that would otherwise dangle off PERSONAL.
    if (!present.has(r.sourceId) || !present.has(r.targetId)) {
      report.danglingRelations.push(r.id);
      continue;
    }
    let kind = RELATIONS[r.kind];
    if (!kind) {
      report.unmappedRelations.push(r.kind);
      kind = 'related';
    }
    edges.push({
      id: r.id,
      sourceId: r.sourceId,
      targetId: r.targetId,
      kind,
      directed: true,
      metadata: { canonicalKind: r.kind },
    });
  }

  // childCount/relationCount are derived here so the tree and Inspector do not
  // each count edges themselves and disagree.
  const childCount: Record<string, number> = {};
  const relationCount: Record<string, number> = {};
  for (const n of nodes) {
    if (n.parentId) childCount[n.parentId] = (childCount[n.parentId] || 0) + 1;
  }
  for (const e of edges) {
    relationCount[e.sourceId] = (relationCount[e.sourceId] || 0) + 1;
    relationCount[e.targetId] = (relationCount[e.targetId] || 0) + 1;
  }
  for (const n of nodes) {
    n.childCount = childCount[n.id] || 0;
    n.relationCount = relationCount[n.id] || 0;
  }

  return {
    snapshot: { revision: dto.revision, nodes, edges },
    report,
    allEntities: entities,
  };
}

/**
 * Counts for the Dev panel, derived from the STORE rather than restated.
 *
 * Typed exactly as the backend types them: `declaredNotBuiltPrimaryAgents` is
 * its own figure and can never be read as "N unbuilt agents" when most of the
 * unbuilt things are surfaces.
 */
export function deriveLiveCounts(result: AdapterResult) {
  const byKind: Record<string, number> = {};
  for (const n of result.snapshot.nodes) {
    const k = String((n.metadata as any)?.canonicalKind || n.kind);
    byKind[k] = (byKind[k] || 0) + 1;
  }
  const nonLive = result.allEntities.filter((e) => !isLiveTopology(e));
  const memoryRecords = result.snapshot.nodes.reduce((acc, n) => {
    const m = n.metadata as any;
    // Only genuine memory systems contribute. Log stores are databases and are
    // counted as stores, never as 65k memory particles.
    const isMemory = String(m?.canonicalKind || '').endsWith('-memory');
    return acc + (isMemory && typeof m?.records === 'number' ? m.records : 0);
  }, 0);

  return {
    revision: result.snapshot.revision,
    domains: byKind['domain'] || 0,
    livePrimaryAgents: byKind['primary-agent'] || 0,
    teams: byKind['team'] || 0,
    teamMembers: byKind['team-member'] || 0,
    totalLiveAgentEntities: (byKind['primary-agent'] || 0) + (byKind['team-member'] || 0),
    declaredNotBuiltPrimaryAgents: nonLive.filter(
      (e) => e.kind === 'primary-agent' && e.implementationState === DECLARED_NOT_BUILT).length,
    orphans: nonLive.filter((e) => e.implementationState === ORPHAN_STATE).length,
    osMemory: byKind['os-memory'] || 0,
    domainMemory: byKind['domain-memory'] || 0,
    agentMemory: byKind['agent-memory'] || 0,
    teamMemory: byKind['team-memory'] || 0,
    memoryFiles: byKind['memory-file'] || 0,
    memoryRecords,
    databases: byKind['database'] || 0,
    workflows: byKind['workflow'] || 0,
    models: byKind['model'] || 0,
    services: byKind['service'] || 0,
    surfaces: byKind['surface'] || 0,
    totalNodes: result.snapshot.nodes.length,
    totalRelations: result.snapshot.edges.length,
    labNodes: 0,
  };
}

/**
 * Diff two canonical snapshots into a patch (D24).
 *
 * Nodes are compared by a structural fingerprint that INCLUDES the memory
 * temporal fields, so a silo whose mtime or record count moved is reported as an
 * update — that is the "WHEN memory changes" signal reaching the graph. Nodes
 * whose fingerprint is unchanged are not emitted at all, which is what keeps a
 * patch small and the layout stable.
 */
export function diffSnapshots(
  prev: RexyGraphSnapshot | null,
  next: RexyGraphSnapshot,
) {
  const upsertNodes: RexyGraphNodeRecord[] = [];
  const removeNodeIds: string[] = [];
  const upsertEdges: RexyGraphEdgeRecord[] = [];
  const removeEdgeIds: string[] = [];

  const fingerprint = (n: RexyGraphNodeRecord) => {
    const m = (n.metadata || {}) as any;
    return [n.label, n.kind, n.parentId || '', m.implementationState || '',
      m.mtime ?? '', m.records ?? '', m.bytes ?? '', n.childCount ?? ''].join('|');
  };

  const prevNodes = new Map((prev?.nodes || []).map((n) => [n.id, n]));
  const nextNodes = new Map(next.nodes.map((n) => [n.id, n]));

  nextNodes.forEach((n, id) => {
    const before = prevNodes.get(id);
    if (!before || fingerprint(before) !== fingerprint(n)) upsertNodes.push(n);
  });
  prevNodes.forEach((_n, id) => {
    if (!nextNodes.has(id)) removeNodeIds.push(id);
  });

  const prevEdges = new Map((prev?.edges || []).map((e) => [e.id, e]));
  const nextEdges = new Map(next.edges.map((e) => [e.id, e]));
  nextEdges.forEach((e, id) => {
    if (!prevEdges.has(id)) upsertEdges.push(e);
  });
  prevEdges.forEach((_e, id) => {
    if (!nextEdges.has(id)) removeEdgeIds.push(id);
  });

  return { revision: next.revision, upsertNodes, removeNodeIds, upsertEdges, removeEdgeIds };
}
